<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');

const stages = ref([]);
const pipelines = ref([]);
// Pipeline whose stages are listed; follows the lead's stage unless the agent
// picks another funnel to move the lead into.
const viewedPipelineId = ref('');
const agents = ref([]);
const lead = ref(null);
const form = ref(defaultLeadForm());
const noteContent = ref('');
const taskForm = ref(defaultTaskForm());
const attachmentForm = ref(defaultAttachmentForm());
const selectedAttachmentFile = ref(null);
const loading = ref(false);
const savingLead = ref(false);
const savingNote = ref(false);
const savingTask = ref(false);
const savingAttachment = ref(false);
const error = ref('');

const accountId = computed(() => {
  const match = window.location.pathname.match(/\/app\/accounts\/([^/]+)/);
  return match?.[1] || currentChat.value?.account_id || '1';
});

const savedLabels = computed(() => {
  return store.getters['conversationLabels/getConversationLabels'](
    props.conversationId
  );
});

const activeCrmLabel = computed(() => {
  return (savedLabels.value || []).find(label => label.startsWith('crm:'));
});

const selectedStage = computed(() => {
  return stages.value.find(stage => stage.id === form.value.stageId);
});

const activePipelineId = computed(() => {
  return (
    viewedPipelineId.value ||
    selectedStage.value?.pipelineId ||
    pipelines.value[0]?.id ||
    ''
  );
});

const pipelineStages = computed(() => {
  return stages.value.filter(stage => stage.pipelineId === activePipelineId.value);
});

const selectStage = stage => {
  if (savingLead.value) return;
  form.value.stageId = stage.id;
  viewedPipelineId.value = '';
};

const selectedAgent = computed(() => {
  return agents.value.find(agent => String(agent.id) === form.value.assignedAgentId);
});

const currentStageId = computed(() => {
  return lead.value?.stageId || stageIdFromLabels();
});

const hasLeadChanges = computed(() => {
  const current = formFromLead(lead.value, currentStageId.value, currentChat.value);
  return JSON.stringify(normalizeComparableForm(form.value)) !== JSON.stringify(normalizeComparableForm(current));
});

const leadTasks = computed(() => sortedTasks(lead.value?.tasks || []));
const leadAttachments = computed(() => lead.value?.attachments || []);

const loadContext = async () => {
  loading.value = true;
  error.value = '';

  try {
    await store.dispatch('conversationLabels/get', props.conversationId);
    const [pipelinesPayload, agentsPayload] = await Promise.all([
      crmRequest('/api/pipelines'),
      crmRequest('/api/chatwoot/agents'),
    ]);

    pipelines.value = pipelinesPayload.pipelines || [];
    stages.value = pipelines.value.flatMap(pipeline =>
      (pipeline.stages || []).map(stage => ({ ...stage, pipelineId: pipeline.id }))
    );
    agents.value = agentsPayload.agents || [];
    await loadCrmLead();
    syncSelectedValues();
  } catch (err) {
    error.value = err.message || 'Erro ao carregar CRM';
  } finally {
    loading.value = false;
  }
};

const loadCrmLead = async () => {
  const conversationId = crmConversationId();
  if (!conversationId) return;

  try {
    const payload = await crmRequest(
      `/api/leads/by-chatwoot-conversation/${encodeURIComponent(conversationId)}`
    );
    lead.value = payload.lead || null;
  } catch {
    lead.value = null;
  }
};

const syncSelectedValues = () => {
  form.value = formFromLead(lead.value, currentStageId.value || stages.value[0]?.id || '', currentChat.value);
};

const stageIdFromLabels = () => {
  const stage = stages.value.find(item => item.chatwootLabel === activeCrmLabel.value);
  return stage?.id || '';
};

const saveLeadDetails = async () => {
  savingLead.value = true;
  error.value = '';

  try {
    let crmLead = await ensureLead();
    const stageChanged = selectedStage.value && selectedStage.value.id !== crmLead.stageId;
    const nextLabels = stageChanged ? labelsForSelectedStage() : savedLabels.value || [];

    if (stageChanged) {
      await store.dispatch('conversationLabels/update', {
        conversationId: props.conversationId,
        labels: nextLabels,
      });

      const stagePayload = await crmRequest(`/api/leads/${crmLead.id}/stage`, {
        method: 'PATCH',
        body: {
          stageId: selectedStage.value.id,
          actor: 'chatwoot-ui',
          reason: 'conversation-sidebar',
        },
      });
      crmLead = stagePayload.lead || crmLead;
    }

    const payload = await crmRequest(`/api/leads/${crmLead.id}`, {
      method: 'PATCH',
      body: buildLeadUpdatePayload(nextLabels),
    });

    lead.value = payload.lead || crmLead;
    syncSelectedValues();
    useAlert('Dados do CRM salvos');
  } catch (err) {
    error.value = err.message || 'Erro ao salvar dados do CRM';
  } finally {
    savingLead.value = false;
  }
};

const saveCrmNote = async () => {
  const content = noteContent.value.trim();
  if (!content) return;

  savingNote.value = true;
  error.value = '';

  try {
    const crmLead = await ensureLead();
    const payload = await crmRequest(`/api/leads/${crmLead.id}/notes`, {
      method: 'POST',
      body: {
        content,
        visibility: 'internal',
        actor: 'chatwoot-ui',
      },
    });

    lead.value = payload.lead || lead.value;
    noteContent.value = '';
    useAlert('Nota salva no CRM');
  } catch (err) {
    error.value = err.message || 'Erro ao salvar nota no CRM';
  } finally {
    savingNote.value = false;
  }
};

const saveCrmTask = async () => {
  const title = taskForm.value.title.trim();
  if (!title) return;

  savingTask.value = true;
  error.value = '';

  try {
    const crmLead = await ensureLead();
    await crmRequest('/api/tasks', {
      method: 'POST',
      body: {
        leadId: crmLead.id,
        title,
        dueAt: normalizeDateTimeLocal(taskForm.value.dueAt),
        priority: taskForm.value.priority || 'normal',
        description: emptyToNull(taskForm.value.description),
        assignees: selectedTaskAssignees(),
        actor: 'chatwoot-ui',
      },
    });

    taskForm.value = defaultTaskForm();
    await loadCrmLead();
    syncSelectedValues();
    useAlert('Tarefa criada no CRM');
  } catch (err) {
    error.value = err.message || 'Erro ao criar tarefa no CRM';
  } finally {
    savingTask.value = false;
  }
};

const updateCrmTaskStatus = async (task, status) => {
  if (!task?.id) return;

  savingTask.value = true;
  error.value = '';

  try {
    await crmRequest(`/api/tasks/${task.id}`, {
      method: 'PATCH',
      body: {
        status,
        actor: 'chatwoot-ui',
      },
    });
    await loadCrmLead();
    syncSelectedValues();
    useAlert(status === 'done' ? 'Tarefa concluida' : 'Tarefa reaberta');
  } catch (err) {
    error.value = err.message || 'Erro ao atualizar tarefa no CRM';
  } finally {
    savingTask.value = false;
  }
};

const saveCrmAttachment = async () => {
  const file = selectedAttachmentFile.value;
  const url = emptyToNull(attachmentForm.value.url);
  if (!file && !url) return;

  savingAttachment.value = true;
  error.value = '';

  try {
    const crmLead = await ensureLead();
    const description = emptyToNull(attachmentForm.value.description);

    if (file) {
      await crmRequest('/api/attachments/upload', {
        method: 'POST',
        body: {
          leadId: crmLead.id,
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          size: file.size,
          description,
          contentBase64: await fileToDataUrl(file),
          actor: 'chatwoot-ui',
        },
      });
    } else {
      await crmRequest('/api/attachments', {
        method: 'POST',
        body: {
          leadId: crmLead.id,
          fileName: fileNameFromUrl(url),
          url,
          description,
          actor: 'chatwoot-ui',
        },
      });
    }

    attachmentForm.value = defaultAttachmentForm();
    selectedAttachmentFile.value = null;
    await loadCrmLead();
    syncSelectedValues();
    useAlert('Anexo salvo no CRM');
  } catch (err) {
    error.value = err.message || 'Erro ao salvar anexo no CRM';
  } finally {
    savingAttachment.value = false;
  }
};

const onAttachmentFileChange = event => {
  selectedAttachmentFile.value = event.target.files?.[0] || null;
};

const ensureLead = async () => {
  if (lead.value?.id) return lead.value;
  const payload = await upsertLead(savedLabels.value || []);
  lead.value = payload.result?.lead || null;
  if (!lead.value?.id) throw new Error('Nao foi possivel localizar o lead no CRM');
  return lead.value;
};

const upsertLead = async labels => {
  return await crmRequest('/api/automation/chatwoot/upsert-lead', {
    method: 'POST',
    body: buildCrmPayload(labels),
  });
};

const buildCrmPayload = labels => {
  const chat = currentChat.value || {};
  const sender = chat.meta?.sender || chat.contact || {};
  const additionalAttributes = {
    ...(sender.additional_attributes || {}),
    ...(chat.additional_attributes || {}),
  };
  const assignee = chat.meta?.assignee || {};

  return {
    accountId: accountId.value,
    chatwootConversationId: firstPresent(chat.display_id, chat.displayId, crmConversationId()),
    chatwootConversationInternalId: firstPresent(chat.id, chat.conversation_id, chat.uuid, props.conversationId),
    chatwootContactId: firstPresent(sender.id, chat.contact_id, chat.contactId),
    identifier: firstPresent(sender.identifier, chat.identifier, additionalAttributes.identifier),
    name: firstPresent(sender.name, sender.available_name, chat.name, 'Lead Chatwoot'),
    phone: firstPresent(
      sender.phone_number,
      sender.phone,
      additionalAttributes.phone_number,
      additionalAttributes.phone,
      phoneFromIdentifier(sender.identifier)
    ),
    email: firstPresent(sender.email, additionalAttributes.email),
    labels,
    source: chat.meta?.channel || 'chatwoot',
    assignedTo: assignee.available_name || assignee.name,
    assignedAgentId: assignee.id,
    assignedAgentEmail: assignee.email,
    customFields: {
      chatwootInboxId: chat.inbox_id,
      chatwootStatus: chat.status,
      chatwootAssigneeId: assignee.id,
      chatwootTeamId: chat.meta?.team?.id,
    },
    actor: 'chatwoot-ui',
  };
};

const buildLeadUpdatePayload = labels => {
  const agent = selectedAgent.value;
  return {
    name: emptyToNull(form.value.name) || 'Lead sem nome',
    phone: emptyToNull(form.value.phone),
    email: emptyToNull(form.value.email),
    company: emptyToNull(form.value.company),
    offerName: emptyToNull(form.value.offerName),
    source: emptyToNull(form.value.source) || 'chatwoot',
    temperature: emptyToNull(form.value.temperature),
    value: parseMoney(form.value.value),
    currency: form.value.currency || 'BRL',
    nextStep: emptyToNull(form.value.nextStep),
    nextFollowUpAt: emptyToNull(form.value.nextFollowUpAt),
    lossReason: emptyToNull(form.value.lossReason),
    commercialNotes: emptyToNull(form.value.commercialNotes),
    assignedTo: agent ? displayAgentName(agent) : null,
    assignedAgentId: agent ? String(agent.id) : null,
    assignedAgentEmail: agent?.email || null,
    labels,
    actor: 'chatwoot-ui',
  };
};

const labelsForSelectedStage = () => {
  return [
    ...(savedLabels.value || []).filter(label => !label.startsWith('crm:')),
    selectedStage.value?.chatwootLabel,
  ].filter(Boolean);
};

const crmConversationId = () => {
  return currentChat.value?.id || props.conversationId;
};

const crmRequest = async (path, options = {}) => {
  const url = withAccountId(`/crm${path}`);
  const response = await fetch(url, {
    method: options.method || 'GET',
    headers: options.body ? { 'content-type': 'application/json' } : {},
    body: options.body ? JSON.stringify({ accountId: accountId.value, ...options.body }) : undefined,
  });
  const payload = await response.json();
  if (!response.ok) throw new Error(payload.error || 'Erro na requisicao do CRM');
  return payload;
};

const withAccountId = url => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}accountId=${encodeURIComponent(accountId.value)}`;
};

const displayAgentName = agent => {
  return String(agent?.available_name || agent?.name || agent?.email || '').trim();
};

const selectedTaskAssignees = () => {
  return taskForm.value.assignedAgentIds
    .map(agentId => agents.value.find(agent => String(agent.id) === String(agentId)))
    .filter(Boolean)
    .map(agent => ({
      agentId: String(agent.id),
      name: displayAgentName(agent),
      email: agent.email || null,
    }));
};

function defaultLeadForm() {
  return {
    stageId: '',
    name: '',
    phone: '',
    email: '',
    company: '',
    offerName: '',
    source: 'chatwoot',
    temperature: '',
    value: '',
    currency: 'BRL',
    nextStep: '',
    nextFollowUpAt: '',
    lossReason: '',
    assignedAgentId: '',
    commercialNotes: '',
  };
}

function defaultTaskForm() {
  return {
    title: '',
    dueAt: '',
    priority: 'normal',
    assignedAgentIds: [],
    description: '',
  };
}

function defaultAttachmentForm() {
  return {
    url: '',
    description: '',
  };
}

function formFromLead(crmLead, stageId, chat) {
  const sender = chat?.meta?.sender || chat?.contact || {};
  const additionalAttributes = {
    ...(sender.additional_attributes || {}),
    ...(chat?.additional_attributes || {}),
  };
  const assignee = chat?.meta?.assignee || {};

  return {
    stageId: stageId || crmLead?.stageId || '',
    name: crmLead?.name || firstPresent(sender.name, sender.available_name, chat?.name) || '',
    phone: crmLead?.phone || firstPresent(sender.phone_number, sender.phone, additionalAttributes.phone_number, additionalAttributes.phone, phoneFromIdentifier(sender.identifier)) || '',
    email: crmLead?.email || firstPresent(sender.email, additionalAttributes.email) || '',
    company: crmLead?.company || crmLead?.customFields?.company || '',
    offerName: crmLead?.offerName || crmLead?.customFields?.offerName || '',
    source: crmLead?.source || chat?.meta?.channel || 'chatwoot',
    temperature: crmLead?.temperature || '',
    value: formatMoneyInput(crmLead?.value),
    currency: crmLead?.currency || 'BRL',
    nextStep: crmLead?.nextStep || '',
    nextFollowUpAt: toDateTimeLocal(crmLead?.nextFollowUpAt),
    lossReason: crmLead?.lossReason || '',
    assignedAgentId: String(crmLead?.assignedAgentId || assignee.id || ''),
    commercialNotes: crmLead?.commercialNotes || '',
  };
}

function normalizeComparableForm(input) {
  return {
    ...input,
    value: String(parseMoney(input.value) || ''),
    nextFollowUpAt: emptyToNull(input.nextFollowUpAt) || '',
  };
}

function emptyToNull(value) {
  const normalized = String(value || '').trim();
  return normalized ? normalized : null;
}

function firstPresent(...values) {
  return values.find(value => value !== undefined && value !== null && value !== '') || null;
}

function phoneFromIdentifier(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length >= 10 ? digits : null;
}

function parseMoney(value) {
  const normalized = String(value || '')
    .replace(/[^\d,.-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.')
    .trim();
  if (!normalized) return null;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

function formatMoneyInput(value) {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(number);
}

function formatLeadValue() {
  const parsed = parseMoney(form.value.value);
  form.value.value = parsed === null ? '' : formatMoneyInput(parsed);
}

function toDateTimeLocal(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function normalizeDateTimeLocal(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function sortedTasks(tasks = []) {
  return [...tasks].sort((first, second) => {
    if (first.status !== second.status) return first.status === 'open' ? -1 : 1;
    return futureDateValue(first.dueAt) - futureDateValue(second.dueAt);
  });
}

function futureDateValue(value) {
  const timestamp = new Date(value || 0).getTime();
  return Number.isNaN(timestamp) || !value ? Number.MAX_SAFE_INTEGER : timestamp;
}

function formatDateTime(value) {
  if (!value) return 'Sem prazo';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function priorityLabel(value) {
  return {
    high: 'Alta',
    normal: 'Normal',
    low: 'Baixa',
  }[value] || 'Normal';
}

function isTaskOverdue(task) {
  if (task.status !== 'open' || !task.dueAt) return false;
  const dueAt = new Date(task.dueAt).getTime();
  return Number.isFinite(dueAt) && dueAt < Date.now();
}

function taskAssigneeLabel(task) {
  if (task.assignees?.length) {
    return task.assignees.map(assignee => assignee.name || assignee.email || assignee.agentId).filter(Boolean).join(', ');
  }
  return task.assignedTo || 'Sem responsavel';
}

function attachmentHref(url) {
  if (!url) return '#';
  if (url.startsWith('/api/')) return `/crm${url}?accountId=${encodeURIComponent(accountId.value)}`;
  return url;
}

function formatFileSize(size) {
  const bytes = Number(size || 0);
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileNameFromUrl(value) {
  try {
    const pathname = new URL(value).pathname;
    return decodeURIComponent(pathname.split('/').filter(Boolean).pop() || '') || 'anexo';
  } catch {
    return 'anexo';
  }
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Nao foi possivel ler o arquivo.'));
    reader.readAsDataURL(file);
  });
}

watch(() => props.conversationId, loadContext);
watch([activeCrmLabel, stages], syncSelectedValues);

onMounted(loadContext);
</script>

<template>
  <div class="crm-sidebar">
    <div v-if="loading" class="crm-skeleton" role="status" aria-busy="true">
      <div class="crm-panel">
        <div class="bone bone-sm w-24" />
        <div class="crm-chips">
          <span v-for="n in 5" :key="n" class="bone bone-chip" />
        </div>
        <div class="bone bone-input" />
      </div>
      <div class="crm-panel">
        <div class="bone bone-sm w-32" />
        <div class="bone bone-input" />
        <div class="bone bone-input" />
      </div>
    </div>
    <template v-else>
      <form class="crm-form" @submit.prevent="saveLeadDetails">
        <div class="crm-panel crm-panel-hero">
          <div class="crm-panel-header">
            <h4>Etapa do funil</h4>
            <select
              v-if="pipelines.length > 1"
              v-model="viewedPipelineId"
              class="crm-pipeline-select"
              aria-label="Funil"
              :disabled="savingLead"
            >
              <option value="" disabled hidden>
                {{ pipelines.find(p => p.id === activePipelineId)?.name }}
              </option>
              <option v-for="pipeline in pipelines" :key="pipeline.id" :value="pipeline.id">
                {{ pipeline.name }}
              </option>
            </select>
          </div>

          <p v-if="viewedPipelineId && viewedPipelineId !== selectedStage?.pipelineId" class="crm-hint">
            Escolha a etapa para mover o lead para este funil.
          </p>

          <div class="crm-chips" role="radiogroup" aria-label="Etapa do funil">
            <button
              v-for="stage in pipelineStages"
              :key="stage.id"
              type="button"
              role="radio"
              class="crm-chip"
              :class="{ 'is-active': stage.id === form.stageId }"
              :aria-checked="stage.id === form.stageId"
              :disabled="savingLead"
              @click="selectStage(stage)"
            >
              <span class="crm-chip-dot" :style="{ backgroundColor: stage.color || '#2357C6' }" />
              {{ stage.name }}
            </button>
          </div>

          <label class="crm-field" for="crm-agent-select">
            <span>Responsável</span>
            <select
              id="crm-agent-select"
              v-model="form.assignedAgentId"
              :disabled="savingLead"
            >
              <option value="">Sem responsável</option>
              <option v-for="agent in agents" :key="agent.id" :value="String(agent.id)">
                {{ displayAgentName(agent) }}
              </option>
            </select>
          </label>
        </div>

        <details class="crm-panel crm-collapsible">
          <summary class="crm-panel-header">
            <h4>Dados do cliente</h4>
            <svg class="crm-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
          </summary>

          <label class="crm-field">
            <span>Nome</span>
            <input v-model="form.name" :disabled="savingLead" autocomplete="name">
          </label>

          <div class="crm-grid">
            <label class="crm-field">
              <span>Telefone</span>
              <input v-model="form.phone" :disabled="savingLead" autocomplete="tel">
            </label>
            <label class="crm-field">
              <span>Email</span>
              <input v-model="form.email" type="email" :disabled="savingLead" autocomplete="email">
            </label>
          </div>

          <label class="crm-field">
            <span>Empresa</span>
            <input v-model="form.company" :disabled="savingLead" autocomplete="organization">
          </label>
        </details>

        <details class="crm-panel crm-collapsible" open>
          <summary class="crm-panel-header">
            <h4>Oportunidade</h4>
            <svg class="crm-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
          </summary>

          <label class="crm-field">
            <span>Produto ou serviço</span>
            <input
              v-model="form.offerName"
              :disabled="savingLead"
              placeholder="Ex: Automação de WhatsApp"
            >
          </label>

          <div class="crm-grid">
            <label class="crm-field">
              <span>Origem</span>
              <select v-model="form.source" :disabled="savingLead">
                <option value="chatwoot">Chatwoot</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="instagram">Instagram</option>
                <option value="site">Site</option>
                <option value="indicacao">Indicação</option>
                <option value="trafego-pago">Tráfego pago</option>
                <option v-if="form.source && !['chatwoot', 'whatsapp', 'instagram', 'site', 'indicacao', 'trafego-pago'].includes(form.source)" :value="form.source">
                  {{ form.source }}
                </option>
              </select>
            </label>

            <label class="crm-field">
              <span>Temperatura</span>
              <select v-model="form.temperature" :disabled="savingLead">
                <option value="">Não definida</option>
                <option value="quente">Quente</option>
                <option value="morno">Morno</option>
                <option value="frio">Frio</option>
              </select>
            </label>
          </div>

          <div class="crm-grid">
            <label class="crm-field">
              <span>Valor</span>
              <input
                v-model="form.value"
                type="text"
                inputmode="decimal"
                :disabled="savingLead"
                placeholder="R$ 0,00"
                @blur="formatLeadValue"
              >
            </label>
            <label class="crm-field">
              <span>Moeda</span>
              <select v-model="form.currency" :disabled="savingLead">
                <option value="BRL">BRL</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </label>
          </div>
        </details>

        <details class="crm-panel crm-collapsible">
          <summary class="crm-panel-header">
            <h4>Próxima ação</h4>
            <svg class="crm-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
          </summary>

          <label class="crm-field">
            <span>Próximo passo</span>
            <input
              v-model="form.nextStep"
              :disabled="savingLead"
              placeholder="Ex: Enviar proposta"
            >
          </label>

          <label class="crm-field">
            <span>Data do próximo contato</span>
            <input v-model="form.nextFollowUpAt" type="datetime-local" :disabled="savingLead">
          </label>

          <label class="crm-field">
            <span>Motivo de perda</span>
            <input
              v-model="form.lossReason"
              :disabled="savingLead"
              placeholder="Se perdido, registre o motivo"
            >
          </label>

          <label class="crm-field">
            <span>Observações comerciais</span>
            <textarea
              v-model="form.commercialNotes"
              rows="4"
              :disabled="savingLead"
              placeholder="Contexto, dores, objeções, orçamento, decisores…"
            />
          </label>
        </details>

        <div class="crm-actions crm-save-row">
          <button type="submit" class="crm-primary-button" :disabled="savingLead || !hasLeadChanges">
            {{ savingLead ? 'Salvando...' : 'Salvar dados do CRM' }}
          </button>
          <span v-if="lead?.updatedAt" class="crm-muted">
            Atualizado {{ new Date(lead.updatedAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) }}
          </span>
        </div>
      </form>

      <details class="crm-panel crm-collapsible">
        <summary class="crm-panel-header">
          <h4>Tarefas</h4>
          <span class="crm-count">{{ leadTasks.length }}</span>
          <svg class="crm-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
        </summary>

        <form class="crm-task-form" @submit.prevent="saveCrmTask">
          <label class="crm-field">
            <span>Tarefa</span>
            <input
              v-model="taskForm.title"
              :disabled="savingTask"
              placeholder="Ex: Enviar proposta"
              required
            >
          </label>

          <div class="crm-grid">
            <label class="crm-field">
              <span>Prazo</span>
              <input v-model="taskForm.dueAt" type="datetime-local" :disabled="savingTask">
            </label>
            <label class="crm-field">
              <span>Prioridade</span>
              <select v-model="taskForm.priority" :disabled="savingTask">
                <option value="normal">Normal</option>
                <option value="high">Alta</option>
                <option value="low">Baixa</option>
              </select>
            </label>
          </div>

          <label class="crm-field">
            <span>Responsáveis</span>
            <select v-model="taskForm.assignedAgentIds" multiple :disabled="savingTask || !agents.length">
              <option v-for="agent in agents" :key="agent.id" :value="String(agent.id)">
                {{ displayAgentName(agent) }}
              </option>
            </select>
          </label>

          <label class="crm-field">
            <span>Descrição</span>
            <textarea
              v-model="taskForm.description"
              rows="2"
              :disabled="savingTask"
              placeholder="Detalhes da solicitação ou combinados"
            />
          </label>

          <button
            type="submit"
            class="crm-secondary-button"
            :disabled="savingTask || !taskForm.title.trim()"
          >
            {{ savingTask ? 'Criando tarefa...' : 'Criar tarefa' }}
          </button>
        </form>

        <div class="crm-task-list">
          <article
            v-for="task in leadTasks"
            :key="task.id"
            class="crm-task-card"
            :class="{ 'is-done': task.status === 'done', 'is-overdue': isTaskOverdue(task) }"
          >
            <div>
              <strong>{{ task.title }}</strong>
              <span>{{ formatDateTime(task.dueAt) }} - {{ priorityLabel(task.priority) }}</span>
              <span>{{ taskAssigneeLabel(task) }}</span>
            </div>
            <button
              type="button"
              class="crm-secondary-button"
              :disabled="savingTask"
              @click="updateCrmTaskStatus(task, task.status === 'done' ? 'open' : 'done')"
            >
              {{ task.status === 'done' ? 'Reabrir' : 'Concluir' }}
            </button>
          </article>
          <p v-if="!leadTasks.length" class="crm-muted">Sem tarefas vinculadas.</p>
        </div>
      </details>

      <details class="crm-panel crm-collapsible">
        <summary class="crm-panel-header">
          <h4>Anexos</h4>
          <span class="crm-count">{{ leadAttachments.length }}</span>
          <svg class="crm-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
        </summary>

        <form class="crm-task-form" @submit.prevent="saveCrmAttachment">
          <label class="crm-field">
            <span>Arquivo ou imagem</span>
            <input type="file" :disabled="savingAttachment" @change="onAttachmentFileChange">
          </label>
          <label class="crm-field">
            <span>Ou URL externa</span>
            <input v-model="attachmentForm.url" type="url" :disabled="savingAttachment" placeholder="https://...">
          </label>
          <label class="crm-field">
            <span>Descrição</span>
            <input v-model="attachmentForm.description" :disabled="savingAttachment" placeholder="Ex: proposta enviada">
          </label>
          <button
            type="submit"
            class="crm-secondary-button"
            :disabled="savingAttachment || (!selectedAttachmentFile && !attachmentForm.url.trim())"
          >
            {{ savingAttachment ? 'Salvando anexo...' : 'Salvar anexo' }}
          </button>
        </form>

        <div class="crm-attachment-list">
          <article v-for="attachment in leadAttachments" :key="attachment.id" class="crm-attachment-card">
            <div>
              <strong>{{ attachment.fileName }}</strong>
              <span>
                {{ [attachment.mimeType, formatFileSize(attachment.size)].filter(Boolean).join(' - ') || 'Anexo' }}
              </span>
            </div>
            <a class="crm-secondary-link" :href="attachmentHref(attachment.url)" target="_blank" rel="noopener noreferrer">
              Abrir
            </a>
          </article>
          <p v-if="!leadAttachments.length" class="crm-muted">Sem anexos ainda.</p>
        </div>
      </details>

      <details class="crm-panel crm-collapsible crm-note-panel">
        <summary class="crm-panel-header">
          <h4>Nota interna</h4>
          <svg class="crm-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
        </summary>

        <textarea
          id="crm-note"
          v-model="noteContent"
          rows="3"
          placeholder="Contexto comercial, próximo passo ou observação…"
          :disabled="savingNote"
        />
        <button
          type="button"
          class="crm-secondary-button"
          :disabled="savingNote || !noteContent.trim()"
          @click="saveCrmNote"
        >
          {{ savingNote ? 'Salvando nota...' : 'Salvar nota' }}
        </button>
      </details>

      <p v-if="error" class="crm-error">{{ error }}</p>
    </template>
  </div>
</template>

<style scoped>
/* bluezapy look: white cards on the warm sand background, brand blue actions.
   Colors come from the theme variables so dark mode keeps working. */
.crm-sidebar,
.crm-skeleton {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.25rem 0 0.75rem;
}

.crm-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.crm-panel {
  background: rgb(var(--surface-2));
  border: 1px solid rgb(var(--border-weak));
  border-radius: 1rem;
  box-shadow: 0 1px 0 rgba(31, 27, 23, 0.03), 0 4px 14px rgba(31, 27, 23, 0.04);
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1rem;
}

.crm-panel-hero {
  gap: 0.75rem;
}

.crm-panel-header {
  align-items: center;
  display: flex;
  gap: 0.625rem;
  justify-content: space-between;
}

.crm-panel-header h4 {
  color: rgb(var(--slate-12));
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.25;
  margin: 0;
}

/* Collapsible sections */
.crm-collapsible {
  gap: 0;
  padding: 0;
}

.crm-collapsible > summary {
  cursor: pointer;
  list-style: none;
  padding: 0.875rem 1rem;
  user-select: none;
}

.crm-collapsible > summary::-webkit-details-marker {
  display: none;
}

.crm-collapsible > summary:hover {
  background: rgb(var(--slate-2));
  border-radius: 1rem;
}

.crm-collapsible > summary h4 {
  flex: 1;
}

.crm-collapsible[open] > summary {
  border-bottom: 1px solid rgb(var(--border-weak));
  border-radius: 1rem 1rem 0 0;
}

.crm-collapsible > :not(summary) {
  margin: 0.875rem 1rem 0;
}

.crm-collapsible > :last-child {
  margin-bottom: 1rem;
}

.crm-chevron {
  color: rgb(var(--slate-10));
  flex: 0 0 auto;
  transition: transform 150ms ease;
}

.crm-collapsible[open] .crm-chevron {
  transform: rotate(180deg);
}

.crm-count {
  background: rgb(var(--slate-3));
  border-radius: 999px;
  color: rgb(var(--slate-11));
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  padding: 0.25rem 0.5rem;
}

/* Pipeline + stage chips */
.crm-pipeline-select {
  background: rgb(var(--surface-2));
  border: 1px solid rgb(var(--border-strong));
  border-radius: 0.625rem;
  color: rgb(var(--slate-12));
  font-size: 0.8125rem;
  font-weight: 600;
  max-width: 55%;
  padding: 0.3125rem 1.75rem 0.3125rem 0.625rem;
}

.crm-hint {
  color: rgb(var(--blue-11));
  font-size: 0.75rem;
  font-weight: 600;
  margin: 0;
}

.crm-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.crm-chip {
  align-items: center;
  background: rgb(var(--surface-2));
  border: 1px solid rgb(var(--border-strong));
  border-radius: 999px;
  color: rgb(var(--slate-11));
  cursor: pointer;
  display: inline-flex;
  font-size: 0.8125rem;
  font-weight: 600;
  gap: 0.4375rem;
  line-height: 1;
  padding: 0.5rem 0.75rem;
  transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease;
}

.crm-chip:hover:not(:disabled) {
  background: rgb(var(--slate-2));
  border-color: rgb(var(--slate-7));
  color: rgb(var(--slate-12));
}

.crm-chip.is-active {
  background: rgb(var(--slate-12));
  border-color: transparent;
  color: rgb(var(--slate-1));
}

.crm-chip:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.crm-chip-dot {
  border-radius: 999px;
  flex: 0 0 auto;
  height: 0.5rem;
  width: 0.5rem;
}

/* Fields */
.crm-grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.crm-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.crm-field span {
  color: rgb(var(--slate-11));
  font-size: 0.75rem;
  font-weight: 600;
}

.crm-field input,
.crm-field select,
.crm-field textarea,
.crm-note-panel textarea {
  background: rgb(var(--surface-2));
  border: 1px solid rgb(var(--border-strong));
  border-radius: 0.75rem;
  box-shadow: none;
  color: rgb(var(--slate-12));
  font-size: 0.875rem;
  line-height: 1.4;
  min-height: 2.5rem;
  min-width: 0;
  outline: none;
  padding: 0.5625rem 0.75rem;
  transition: background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease;
}

.crm-field input::placeholder,
.crm-field textarea::placeholder,
.crm-note-panel textarea::placeholder {
  color: rgb(var(--slate-9));
}

.crm-field input:hover,
.crm-field select:hover,
.crm-field textarea:hover,
.crm-note-panel textarea:hover {
  border-color: rgb(var(--slate-8));
}

.crm-field input:focus,
.crm-field select:focus,
.crm-field textarea:focus,
.crm-note-panel textarea:focus {
  border-color: rgb(var(--blue-9));
  box-shadow: 0 0 0 4px rgba(var(--blue-9), 0.15);
}

.crm-field input:disabled,
.crm-field select:disabled,
.crm-field textarea:disabled,
.crm-note-panel textarea:disabled {
  background: rgb(var(--slate-2));
  cursor: not-allowed;
  opacity: 0.75;
}

.crm-field textarea,
.crm-note-panel textarea {
  resize: vertical;
}

.crm-field select[multiple] {
  min-height: 5.5rem;
}

.crm-task-form,
.crm-task-list,
.crm-attachment-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.crm-task-card,
.crm-attachment-card {
  align-items: flex-start;
  background: rgb(var(--slate-2));
  border: 1px solid rgb(var(--border-weak));
  border-radius: 0.875rem;
  display: flex;
  gap: 0.625rem;
  justify-content: space-between;
  padding: 0.75rem 0.875rem;
}

.crm-task-card.is-overdue {
  background: rgb(var(--ruby-2));
  border-color: rgb(var(--ruby-6));
}

.crm-task-card.is-done {
  opacity: 0.65;
}

.crm-task-card strong,
.crm-attachment-card strong {
  color: rgb(var(--slate-12));
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.3;
}

.crm-task-card span,
.crm-attachment-card span {
  color: rgb(var(--slate-11));
  display: block;
  font-size: 0.75rem;
  line-height: 1.4;
  margin-top: 0.1875rem;
}

/* Buttons */
.crm-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
}

.crm-primary-button,
.crm-secondary-button,
.crm-secondary-link {
  align-items: center;
  border: 1px solid transparent;
  border-radius: 0.75rem;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.8125rem;
  font-weight: 600;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0.5rem 0.875rem;
  text-decoration: none;
  transition: background-color 150ms ease, border-color 150ms ease, opacity 150ms ease;
}

.crm-primary-button {
  background: rgb(var(--blue-9));
  color: #fff;
  flex: 1;
}

.crm-primary-button:hover:not(:disabled) {
  background: rgb(var(--blue-11));
}

.crm-secondary-button,
.crm-secondary-link {
  align-self: flex-start;
  background: rgb(var(--blue-4));
  color: rgb(var(--blue-11));
  flex: 0 0 auto;
}

.crm-secondary-button:hover:not(:disabled),
.crm-secondary-link:hover {
  background: rgb(var(--blue-5));
}

.crm-primary-button:disabled,
.crm-secondary-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Loading skeleton */
.bone {
  animation: crm-pulse 1.6s ease-in-out infinite;
  background: rgb(var(--slate-4));
  border-radius: 0.5rem;
}

.bone-sm {
  height: 0.875rem;
}

.bone-chip {
  border-radius: 999px;
  display: inline-block;
  height: 2rem;
  width: 5.5rem;
}

.bone-input {
  height: 2.5rem;
  width: 100%;
}

.w-24 {
  width: 6rem;
}

.w-32 {
  width: 8rem;
}

@keyframes crm-pulse {
  50% {
    opacity: 0.55;
  }
}

.crm-save-row .crm-primary-button {
  flex-basis: 100%;
}

.crm-save-row .crm-muted {
  text-align: center;
  width: 100%;
}

.crm-muted {
  color: rgb(var(--slate-10));
  font-size: 0.75rem;
}

.crm-error {
  color: rgb(var(--ruby-11));
  font-size: 0.8125rem;
}

@media (max-width: 360px) {
  .crm-grid {
    grid-template-columns: 1fr;
  }
}
</style>
